import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import LossLimitsFormModal from "app/components/LossLimitsFormModal/LossLimitsFormModal";
import RegistrationFormModal from "app/components/RegistrationFormModal/RegistrationFormModal";
import ErrorBoundary from "app/ErrorBoundary/ErrorBoundary";
import { DarkModeHelper } from "app/helpers/darkModeHelper";
import { PersistLanguageHelper } from "app/helpers/persistLanguageHelper";
import { PromoCategoryHelper } from "app/helpers/promoCategoryHelper";
import { TimeDateHelper } from "app/helpers/timeDateHelper";
import { GTM_ID } from "app/utils/const";
import isPublicView from "app/utils/publicViewUtils";
import ENV from "config";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import IdleTimer from "react-idle-timer";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { SessionHelper } from "../../helpers/sessionHelper";
import store from "../../store/store";
import type { EventType } from "../../types/eventType";
import setupSocket from "../../websockets/ws";
import Promo from "../PublicScreen/components/Promo/Promo";
import DownloadSpeed from "./components/DownloadSpeed/DownloadSpeed";
import NavBarMobileFinanceSummary from "./components/NavBarMobileFinanceSummary/NavBarMobileFinanceSummary";
import RouterMainContent from "./components/RouterMainContent/RouterMainContent";
import TACModal from "./components/TACModal/TACModal";
import useSetAffiliateDetailsInCookie from "./hooks/useSetAffiliateDetailsInCookie/useSetAffiliateDetailsInCookie";
import useSetQueryParam from "./hooks/useSetQueryParam/useSetQueryParam";
import "./Router.scss";

function Router(props: {
  session: Record<string, any>;
  user: Record<string, any>;
  showMarketGraphModal: boolean;
  marketGraphModalEventData: EventType;
  downloadSpeed: Record<string, any>;
  setTacAccepted: Function;
  requestContent: Function;
  showTacModal: boolean;
  html: any;
  versionTermsAndConditions: string;
  loadUserSession: Function;
  login: Function;
  setNotificationPlatform: Function;
  loadNotifications: Function;
  purgeBetSlipList: Function;
  updatePromotedCategory: Function;
  isRegistrationModalOpen: boolean;
}) {
  const location = useLocation();
  useSetQueryParam(location, "promoCode");
  useSetAffiliateDetailsInCookie(location);

  const {
    downloadSpeed,
    setTacAccepted,
    session,
    requestContent,
    user,
    showTacModal,
    isRegistrationModalOpen,
  } = props;
  const [idleTimer, setIdleTimer] = useState<any>(null);
  const [wsInitialised, setWsInitialised] = useState<boolean>(false);
  const [isComponentMounted, setIsComponentMounted] = useState<boolean>(false);

  useEffect(() => {
    if (showTacModal === false && user["confirmedTerms"] === false) {
      requestContent();
    }
  }, [showTacModal, user]);

  useEffect(() => {
    TagManager.initialize({ gtmId: GTM_ID });

    if (!SessionHelper.isAuthenticated() && isPublicView()) {
      loginPublicUser();
    }

    if (isPublicView()) {
      PersistLanguageHelper.syncLanguageWithPublicView();
    }
  }, []);

  useEffect(() => {
    if (SessionHelper.isAuthenticated() && !isComponentMounted) {
      PromoCategoryHelper.checkIfPromotedRoute(location);
      setIsComponentMounted(true);
    }
  }, [SessionHelper.isAuthenticated(), isPublicView()]);

  useEffect(() => {
    if (
      !SessionHelper.isAuthenticated() &&
      !isComponentMounted &&
      !isPublicView()
    ) {
      PromoCategoryHelper.checkIfPromotedRoute(location);
      setIsComponentMounted(true);
    }
  }, [!SessionHelper.isAuthenticated(), !isPublicView()]);

  const initiateWebSockets = () => {
    console.log("router initiateWebSockets: wsInitialised", wsInitialised);

    if (SessionHelper.isAuthenticated()) {
      if (!wsInitialised) {
        setupSocket();
        setWsInitialised(true);
      } else {
        console.log("Don't setupSocket");
      }
    }
  };

  !wsInitialised && initiateWebSockets();
  const _onActive = (e: Event) => {
    console.log("user is active reset timout - restart polling", e);
    console.log(
      "time remaining",
      idleTimer !== null && idleTimer.getRemainingTime()
    );
    store.dispatch.sessionModel.updateIsIdle(false);
  };

  const _onIdle = (e: Event) => {
    if (SessionHelper.isAuthenticated()) {
      const time = TimeDateHelper.getUTCFormattedDateTime("HH:mm:ss:SSS");
      console.log("user is idle - stop polling events", e, time);

      store.dispatch.sessionModel.updateIsIdle(true);
    } else {
      console.log("useridle not logged in");
    }
  };

  const onCloseModal = () => {
    console.log("closing modal :");
  };

  if (isEmpty(props.session) && SessionHelper.isAuthenticated()) {
    DarkModeHelper.checkDarkModeStatus();
    // check if public view
    !isPublicView() && props.loadUserSession();
  }

  const loginPublicUser = () => {
    props.login("publicuser", "Password1").catch(error => {
      if (!isPublicView()) {
        window.location.replace(`${ENV.MAIN_SITE_URL}`);
      } else {
        console.warn("Public login failed but still in public view");
      }
    });
  };

  const pathnameIncludesAsian = location.pathname.includes("asian");
  // Setting to false for release 1.1
  const selectedView =
    SessionHelper.isAuthenticated() && pathnameIncludesAsian
      ? "asian-router"
      : "european-router";

  // display loader when transitioning from main UI to public UI as authentication is slow
  if (!SessionHelper.isAuthenticated() && isPublicView()) {
    return (
      <div className={`router public-view__loading`}>
        <LoadingSpinner />
      </div>
    );
  }
  // Handle conditional render for the Promo route
  if (location.pathname === "/promotions/signup") {
    return (
      <div className={`router ${selectedView}`}>
        <Promo />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div
        className={`router ${selectedView} ${
          isRegistrationModalOpen ? "registration-form-modal-open" : ""
        }`}
      >
        {/* once user is logged in start checking network download speed */}
        {SessionHelper.isAuthenticated() && (
          <DownloadSpeed
            downloadSpeed={downloadSpeed}
            selectedView={selectedView}
          />
        )}
        <IdleTimer
          ref={ref => {
            setIdleTimer(ref);
          }}
          element={document}
          onActive={_onActive}
          onIdle={_onIdle}
          timeout={3600000}
        />
        <NavBarMobileFinanceSummary />
        <RouterMainContent location={location} />
        <TACModal
          data={props}
          closeModal={() => onCloseModal()}
          setTacAccepted={() => setTacAccepted(session)}
        />
        <RegistrationFormModal />
        <LossLimitsFormModal />
      </div>
    </ErrorBoundary>
  );
}

const mapStateToProps = state => ({
  loading: state.sessionModel.loadingLogin,
  notificationTotal: state.notificationListModel.total,
  showNotificationDisplay: state.notificationListModel.showNotificationDisplay,
  session: state.sessionModel.session,
  downloadSpeed: state.sessionModel.downloadSpeed,
  user: state.userModel.user,
  showMarketGraphModal: state.marketDataGraphModel.showMarketGraphModal,
  marketGraphModalEventData:
    state.marketDataGraphModel.marketGraphModalEventData,
  showTacModal: state.termsAndConditionsModel.showTacModal,
  html: state.termsAndConditionsModel.html,
  versionTermsAndConditions:
    state.termsAndConditionsModel.versionTermsAndConditions,
  isRegistrationModalOpen: state.registrationFormModel.isRegistrationModalOpen,
});

const mapDispatchToProps = (dispatch: any) => ({
  setTacAccepted: (session: any) => {
    dispatch.termsAndConditionsModel.setTacAccepted(session);
  },
  requestContent: () => {
    dispatch.termsAndConditionsModel.requestContent();
  },
  loadUserSession: () => {
    dispatch.sessionModel.loadSession();
  },
  loadNotifications: () => {
    dispatch.notificationListModel.loadNotificationList();
  },
  setNotificationPlatform: platform => {
    dispatch.notificationListModel.setPlatform(platform);
  },
  purgeBetSlipList: () => {
    dispatch.betSlipListModel.purgeBetSlipList();
  },
  login: (username, password) =>
    dispatch.sessionModel.loginUser({
      username,
      password,
    }),
  updatePromotedCategory: category => {
    dispatch.promoCategoryModel.updatePromotedCategory(category);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Router));
